import React from 'react';
import PropTypes from 'prop-types';
import './WalletCard.scss';

/**
 * Presentational wallet card. The caller owns quantities, labels and all mutations.
 * @param {object} props - Wallet display content and optional actions.
 * @param {React.ReactNode} props.amount - Authoritative displayed quantity.
 * @param {string} [props.currencySymbol] - Currency prefix displayed beside the amount.
 * @param {React.ReactNode} props.caption - Balance or selection description.
 * @param {React.ReactNode} props.brand - Wallet brand.
 * @param {React.ReactNode} [props.category] - Supplied membership category.
 * @param {React.ReactNode} [props.tierIcon] - Category artwork.
 * @param {React.ReactNode} [props.footer] - Additional supplied information.
 * @param {string} [props.actionLabel] - Main action label.
 * @param {Function} [props.onAction] - Explicit wallet action.
 * @param {Function} [props.onEdit] - Optional editing action for wallets that support it.
 * @param {string} [props.editLabel] - Accessible editing label.
 * @param {boolean} [props.disabled] - Disables wallet actions.
 * @returns {React.ReactElement} Wallet card.
 */
export default function WalletCard({
  amount,
  currencySymbol,
  caption,
  brand,
  category,
  tierIcon,
  footer,
  actionLabel,
  onAction,
  onEdit,
  editLabel,
  disabled = false,
}) {
  return (
    <div className="wallet-card">
      <svg className="wallet-card__stars" viewBox="0 0 460 254" aria-hidden="true">
        <path d="M222 6c0 30-8 39-37 39 29 0 37 9 37 39 0-30 8-39 37-39-29 0-37-9-37-39ZM307 65c0 30-8 39-37 39 29 0 37 9 37 39 0-30 8-39 37-39-29 0-37-9-37-39ZM136 92c0 29-8 37-35 37 27 0 35 8 35 37 0-29 8-37 35-37-27 0-35-8-35-37ZM270 151c0 16-5 21-21 21 16 0 21 5 21 21 0-16 5-21 21-21-16 0-21-5-21-21ZM232 186c0 26-8 34-34 34 26 0 34 8 34 34 0-26 8-34 34-34-26 0-34-8-34-34Z" />
      </svg>
      <div className="wallet-card__body">
        <div className="wallet-card__header">
          <div className="wallet-card__amount-row">
            {currencySymbol && <span className="wallet-card__currency">{currencySymbol}</span>}
            <strong className="wallet-card__amount">{amount}</strong>
            {onEdit && (
              <button
                type="button"
                className="wallet-card__edit"
                aria-label={editLabel}
                disabled={disabled}
                onClick={onEdit}
              >
                <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                  <path
                    d="m15 4 5 5M4 20l5-1L21 7l-5-5L4 14Z"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                </svg>
              </button>
            )}
          </div>
          <div className="wallet-card__identity">
            {category && <span className="wallet-card__category">{category}</span>}
            {tierIcon}
            <span className="wallet-card__brand">{brand}</span>
          </div>
        </div>
        <p className="wallet-card__caption">{caption}</p>
        <div className="wallet-card__footer">
          <div className="wallet-card__details">{footer}</div>
          {onAction && (
            <button
              type="button"
              className="wallet-card__action"
              disabled={disabled}
              onClick={onAction}
            >
              {actionLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

WalletCard.propTypes = {
  amount: PropTypes.node,
  currencySymbol: PropTypes.string,
  caption: PropTypes.node.isRequired,
  brand: PropTypes.node.isRequired,
  category: PropTypes.node,
  tierIcon: PropTypes.node,
  footer: PropTypes.node,
  actionLabel: PropTypes.string,
  onAction: PropTypes.func,
  onEdit: PropTypes.func,
  editLabel: PropTypes.string,
  disabled: PropTypes.bool,
};
